import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { CalendarIcon, Eye, EyeOff } from "lucide-react";
import { useCreateAsset, useUpdateAsset } from "@/hooks/useAssets";
import { cn } from "@/lib/utils";
import { AssetCategory, EmployeeAsset } from "@/types/asset";
import { Employee } from "@/types/employee";

interface AssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset?: EmployeeAsset | null;
  categories: AssetCategory[];
  employees: Employee[];
}

export function AssetDialog({ 
  open, 
  onOpenChange, 
  asset, 
  categories,
  employees 
}: AssetDialogProps) {
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  
  const [formData, setFormData] = useState({
    asset_name: "",
    asset_code: "",
    asset_category_id: "",
    employee_id: "",
    asset_type: "physical" as "physical" | "digital" | "credentials",
    serial_number: "",
    description: "",
    condition: "new" as "new" | "good" | "fair" | "poor" | "damaged",
    return_date: null as Date | null,
    renewal_date: null as Date | null,
    access_url: "",
    username: "",
    password: "",
    reminder_days: 30,
    notes: "",
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | null>(null);
  
  useEffect(() => {
    if (asset) {
      setFormData({
        asset_name: asset.asset_name || "",
        asset_code: asset.asset_code || "",
        asset_category_id: asset.asset_category_id || "",
        employee_id: asset.employee_id || "",
        asset_type: asset.asset_type || "physical",
        serial_number: asset.serial_number || "",
        description: asset.description || "",
        condition: asset.condition || "new",
        return_date: asset.return_date ? new Date(asset.return_date) : null,
        renewal_date: asset.renewal_date ? new Date(asset.renewal_date) : null,
        access_url: asset.access_url || "",
        username: asset.username || "",
        password: "",
        reminder_days: asset.reminder_days || 30,
        notes: asset.notes || "",
      });
    } else {
      // Reset form for new asset
      setFormData({
        asset_name: "",
        asset_code: "",
        asset_category_id: "",
        employee_id: "",
        asset_type: "physical",
        serial_number: "",
        description: "",
        condition: "new",
        return_date: null,
        renewal_date: null,
        access_url: "",
        username: "",
        password: "",
        reminder_days: 30,
        notes: "",
      });
    }
  }, [asset]);
  
  useEffect(() => {
    if (formData.asset_category_id) {
      const category = categories.find(c => c.id === formData.asset_category_id);
      setSelectedCategory(category || null);
      if (category) {
        setFormData(prev => ({ ...prev, asset_type: category.asset_type }));
      }
    }
  }, [formData.asset_category_id, categories]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const dataToSubmit: any = {
      ...formData,
      return_date: formData.return_date?.toISOString(),
      renewal_date: formData.renewal_date?.toISOString(),
      assigned_by: asset ? undefined : employees.find(e => e.user_id)?.id,
    };
    
    // Encrypt password if provided
    if (formData.password) {
      dataToSubmit.encrypted_password = btoa(formData.password);
    }
    
    delete dataToSubmit.password;
    
    if (asset) {
      await updateAsset.mutateAsync({ id: asset.id, ...dataToSubmit });
    } else {
      await createAsset.mutateAsync(dataToSubmit);
    }
    
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>{asset ? "تعديل العهدة" : "إضافة عهدة جديدة"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="asset_name">اسم العهدة *</Label>
              <Input
                id="asset_name"
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="asset_code">رمز العهدة</Label>
              <Input
                id="asset_code"
                value={formData.asset_code}
                onChange={(e) => setFormData({ ...formData, asset_code: e.target.value })}
                placeholder="AST-001"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">التصنيف *</Label>
              <Select 
                value={formData.asset_category_id}
                onValueChange={(value) => setFormData({ ...formData, asset_category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر التصنيف" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name_ar}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="employee">الموظف المسؤول *</Label>
              <Select 
                value={formData.employee_id}
                onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر الموظف" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name} - {employee.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {formData.asset_type === "physical" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="serial_number">الرقم التسلسلي</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="condition">حالة العهدة</Label>
                  <Select 
                    value={formData.condition}
                    onValueChange={(value: any) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">جديد</SelectItem>
                      <SelectItem value="good">جيد</SelectItem>
                      <SelectItem value="fair">متوسط</SelectItem>
                      <SelectItem value="poor">ضعيف</SelectItem>
                      <SelectItem value="damaged">تالف</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="return_date">تاريخ الإرجاع المتوقع</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-right",
                        !formData.return_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="ml-2 h-4 w-4" />
                      {formData.return_date ? (
                        format(formData.return_date, "PPP", { locale: ar })
                      ) : (
                        <span>اختر التاريخ</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.return_date || undefined}
                      onSelect={(date) => setFormData({ ...formData, return_date: date || null })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}
          
          {(formData.asset_type === "digital" || formData.asset_type === "credentials") && (
            <>
              <div className="space-y-2">
                <Label htmlFor="access_url">رابط الوصول</Label>
                <Input
                  id="access_url"
                  type="url"
                  value={formData.access_url}
                  onChange={(e) => setFormData({ ...formData, access_url: e.target.value })}
                  placeholder="https://example.com/login"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">اسم المستخدم</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">كلمة المرور</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute left-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="renewal_date">تاريخ التجديد</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-right",
                          !formData.renewal_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="ml-2 h-4 w-4" />
                        {formData.renewal_date ? (
                          format(formData.renewal_date, "PPP", { locale: ar })
                        ) : (
                          <span>اختر التاريخ</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.renewal_date || undefined}
                        onSelect={(date) => setFormData({ ...formData, renewal_date: date || null })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reminder_days">التذكير قبل (أيام)</Label>
                  <Input
                    id="reminder_days"
                    type="number"
                    value={formData.reminder_days}
                    onChange={(e) => setFormData({ ...formData, reminder_days: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="description">الوصف</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={createAsset.isPending || updateAsset.isPending}>
              {asset ? "تحديث" : "إضافة"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}